import React, { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useMst } from '../../model/Root';
import Layout from '../../components/Layout';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddButton from '../../components/Button/AddButton';
import { useNavigate } from 'react-router-dom';
import DeleteIcon from '@mui/icons-material/Delete';
import AlertModal from '../../components/AlertModal';

const ChurchListScreen = observer(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    churchStore: { itemsArray: churches, fetchChurches, setChurchActive, deleteChurch, loading },
    userStore: { user },
  } = useMst();

  const [search, setSearch] = useState('');
  const [isActiveFilter, setIsActiveFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [fetchedOnce, setFetchedOnce] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!user) return;
      try {
        await fetchChurches();
      } finally {
        if (mounted) setFetchedOnce(true);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [fetchChurches, user]);

  const filtered = useMemo(() => {
    const list = Array.isArray(churches) ? churches : [];
    return list
      .filter((c) => {
        if (isActiveFilter === 'active') return c.isActive;
        if (isActiveFilter === 'inactive') return !c.isActive;
        return true;
      })
      .filter((c) => {
        const s = search.trim().toLowerCase();
        if (!s) return true;
        return c.name.toLowerCase().includes(s) || (c.city ? c.city.toLowerCase().includes(s) : false);
      });
  }, [churches, isActiveFilter, search]);

  const refresh = async () => {
    const active = isActiveFilter === 'all' ? undefined : isActiveFilter === 'active';
    await fetchChurches(search || undefined, active);
  };

  const askDelete = (id?: string) => {
    if (!id) return;
    setSelectedId(id);
    setConfirmOpen(true);
  };

  const cancelDelete = () => {
    setSelectedId(null);
    setConfirmOpen(false);
  };

  const confirmDelete = async () => {
    if (!selectedId) return;
    await deleteChurch(selectedId);
    cancelDelete();
    await refresh();
  };

  return (
    <Layout title={t('church.list')}>
      <Grid container spacing={2} mb={2}>
        <Grid item xs={12} md={6}>
          <TextField
            size="small"
            fullWidth
            label={t('search') ?? 'Search'}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void refresh();
            }}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel id="active-filter">{t('status') ?? 'Status'}</InputLabel>
            <Select
              labelId="active-filter"
              label={t('status') ?? 'Status'}
              value={isActiveFilter}
              onChange={(e) => setIsActiveFilter(e.target.value as 'all' | 'active' | 'inactive')}
            >
              <MenuItem value={'all'}>{t('all') ?? 'All'}</MenuItem>
              <MenuItem value={'active'}>{t('active') ?? 'Active'}</MenuItem>
              <MenuItem value={'inactive'}>{t('inactive') ?? 'Inactive'}</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={3} display="flex" gap={1}>
          <Button variant="outlined" onClick={() => void refresh()}>
            {t('filter') ?? 'Filter'}
          </Button>
          <AddButton onClick={() => navigate('/churches/create')} text={t('church.create') ?? 'Create Church'} />
        </Grid>
      </Grid>

      {fetchedOnce && loading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <CircularProgress size={14} />
          <Typography variant="caption" color="GrayText">
            {t('loading') ?? 'Loading'}
          </Typography>
        </Box>
      )}

      {!fetchedOnce && loading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
          <CircularProgress size={20} />
          <Typography variant="body1">{t('loading') ?? 'Loading'}</Typography>
        </Box>
      ) : (
        <>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('church.name') ?? 'Name'}</TableCell>
                <TableCell>{t('church.city') ?? 'City'}</TableCell>
                <TableCell>{t('church.phoneNumber') ?? 'Phone'}</TableCell>
                <TableCell>{t('active') ?? 'Active'}</TableCell>
                <TableCell>{t('updated') ?? 'Updated'}</TableCell>
                <TableCell>{t('actions') ?? 'Actions'}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((c) => (
                <TableRow key={c.id} hover>
                  <TableCell>{c.name}</TableCell>
                  <TableCell>{c.city ?? ''}</TableCell>
                  <TableCell>{c.phoneNumber ?? ''}</TableCell>
                  <TableCell>
                    <Switch
                      checked={!!c.isActive}
                      onChange={(e) => {
                        if (!c.id) return;
                        setChurchActive(c.id, e.target.checked);
                      }}
                    />
                  </TableCell>
                  <TableCell>{c.updatedAt ? new Date(c.updatedAt).toLocaleString() : ''}</TableCell>
                  <TableCell>
                    <Button size="small" variant="text" onClick={() => navigate(`/churches/${c.id}`)}>
                      {t('edit') ?? 'Edit'}
                    </Button>
                    <IconButton size="small" color="error" onClick={() => askDelete(c.id)} aria-label="delete">
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <AlertModal
            open={confirmOpen}
            onClose={cancelDelete}
            title={'delete'}
            leftButtonPress={cancelDelete}
            rightButtonPress={confirmDelete}
          />
        </>
      )}
    </Layout>
  );
});

export default ChurchListScreen;
